import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EMPTY_PLAYBACK_STATE,
  PendingTrackRef,
  PlaybackController,
  PlaybackState,
  SavedTrackRef,
} from './playback-controller';
import { VOYAGE_TRACK_CREDITS } from 'src/app/services/operators/voyageradar-credit-actions';

/** One position report on a vessel's track (source-agnostic). */
export interface VoyageTrackPoint {
  lat: number;
  lng: number;
  /** Course over ground in degrees (0 when unknown). */
  course: number;
  /** ISO/UTC timestamp of the report. */
  timeIso: string;
  /** Speed over ground in knots, when known. */
  sog?: number;
}

/**
 * ── COVERAGE GAPS ───────────────────────────────────────────────────────────
 *
 * A track is a list of position REPORTS, not a route. Two consecutive reports
 * are evidence of two places the vessel was; the line between them is an
 * assertion about a journey nobody observed. That assertion is safe when the
 * reports are seconds apart and false when they are days apart — and the
 * renderer used to draw both identically, because nothing in it read
 * `timeIso`. The result was a solid confident line from Kochi to Chennai,
 * 1,056 km straight over Tamil Nadu, for a vessel that had simply gone dark
 * while rounding the subcontinent.
 *
 * TWO TESTS, because either alone lets the wrong picture through:
 *
 *  - TIME. Beyond `TRACK_GAP_HOURS` we do not know what happened, whatever the
 *    distance covered. This is the test that catches the case above: 1,056 km
 *    over four days implies 5.9 kn, which is a perfectly ordinary speed — a
 *    speed test passes it without complaint.
 *  - IMPLIED SPEED. Below the time limit a leg can still be impossible: no
 *    merchant hull makes `TRACK_MAX_KNOTS`, so a "one hour, 300 km" pair is
 *    two different vessels, a bad fix, or a duplicated MMSI.
 *
 * Both are exported constants rather than literals because they are judgement
 * calls on real data, not physics. If genuine ocean tracks come back too
 * fragmented, `TRACK_GAP_HOURS` is the single number to raise.
 */
export const TRACK_GAP_HOURS = 6;
export const TRACK_MAX_KNOTS = 40;

const EARTH_RADIUS_NM = 3440.065;

/** Great-circle distance in nautical miles — the unit knots are defined in. */
export function trackDistanceNm(a: VoyageTrackPoint, b: VoyageTrackPoint): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Is the leg from `a` to `b` unobserved — a gap rather than a path?
 *
 * An UNREADABLE timestamp counts as a gap. The alternative is to assume
 * continuity from data we could not parse, which is the assumption this whole
 * mechanism exists to stop making. Out-of-order or identical stamps are not
 * gaps: `dtMs <= 0` means the two reports describe the same instant, so there
 * is no interval to have lost coverage over — and the speed test would divide
 * by zero on them.
 */
export function isTrackGap(a: VoyageTrackPoint, b: VoyageTrackPoint): boolean {
  const t0 = new Date(a?.timeIso ?? '').getTime();
  const t1 = new Date(b?.timeIso ?? '').getTime();
  if (!isFinite(t0) || !isFinite(t1)) return true;
  const dtMs = t1 - t0;
  if (dtMs > TRACK_GAP_HOURS * 3600 * 1000) return true;
  if (dtMs <= 0) return false;
  return trackDistanceNm(a, b) / (dtMs / 3600000) > TRACK_MAX_KNOTS;
}

/**
 * Split a track into runs of CONTINUOUS coverage.
 *
 * Every point appears exactly once and order is preserved, so the segments
 * concatenated are the input — the split decides only where the ink lifts.
 * A single point is a legitimate segment: a hull seen once between two long
 * silences is a fact, and dropping it would be a second kind of lying.
 */
export function splitTrackAtGaps(points: VoyageTrackPoint[]): VoyageTrackPoint[][] {
  const list = Array.isArray(points) ? points : [];
  if (!list.length) return [];
  const out: VoyageTrackPoint[][] = [[list[0]]];
  for (let i = 1; i < list.length; i++) {
    if (isTrackGap(list[i - 1], list[i])) out.push([list[i]]);
    else out[out.length - 1].push(list[i]);
  }
  return out;
}

/**
 * The unobserved legs, as endpoint pairs — what the faint connector draws.
 *
 * Derived from the same predicate as the split, so the two can never disagree
 * about where a gap is.
 */
export function trackGapLinks(
  points: VoyageTrackPoint[],
): Array<[VoyageTrackPoint, VoyageTrackPoint]> {
  const list = Array.isArray(points) ? points : [];
  const out: Array<[VoyageTrackPoint, VoyageTrackPoint]> = [];
  for (let i = 1; i < list.length; i++) {
    if (isTrackGap(list[i - 1], list[i])) out.push([list[i - 1], list[i]]);
  }
  return out;
}

/**
 * "4 d 3 h" — how long contact was lost, for the gap marker's tooltip.
 *
 * Coarse on purpose, and coarser the longer it ran: minutes matter for a
 * six-hour dropout and are noise across four days. Returns null when the
 * interval cannot be read, which is the one case where the marker says only
 * that contact broke and refuses to put a number on it.
 */
export function trackGapLabel(a: VoyageTrackPoint, b: VoyageTrackPoint): string | null {
  const t0 = new Date(a?.timeIso ?? '').getTime();
  const t1 = new Date(b?.timeIso ?? '').getTime();
  if (!isFinite(t0) || !isFinite(t1) || t1 <= t0) return null;
  const mins = Math.round((t1 - t0) / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) {
    const rem = mins % 60;
    return rem ? `${hours} h ${rem} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem ? `${days} d ${rem} h` : `${days} d`;
}

/** What the host must draw for the current instant of the virtual clock. */
export interface VoyageTrackFrame {
  lat: number;
  lng: number;
  course: number;
  /** Index of the last track point at or before the virtual clock. */
  index: number;
  /** 0..1 progress between `index` and `index + 1`. */
  frac: number;
  timeIso: string;
  atEnd: boolean;
}

/** The vessel the user has selected on the map. */
export interface VoyageActiveVessel {
  mmsi: string;
  name: string;
  /** Hex colour supplied by the operator (falls back to a default upstream). */
  color: string;
  lat: number;
  lng: number;
  timestampIso: string;
  shipType: string;
}

/** Spans offered for the paid full-track fetch (upstream caps at 90 days). */
export const VOYAGE_TRACK_SPANS: Array<{ label: string; minutes: number }> = [
  { label: '1 h', minutes: 60 },
  { label: '6 h', minutes: 360 },
  { label: '24 h', minutes: 1440 },
  { label: '7 d', minutes: 10080 },
  { label: '30 d', minutes: 43200 },
  { label: '90 d', minutes: 129600 },
];

/**
 * Playback speeds for track mode. Unlike the AOI flip-book (where speed is
 * simulated-seconds per real-second), a vessel track can span an hour or 90
 * days — a fixed sim-time rate would be unusable at one end or the other. Here
 * speed is a plain multiplier: 1x plays the WHOLE track in `BASE_PLAY_MS`,
 * whatever its real duration.
 */
export const VOYAGE_TRACK_SPEEDS = [0.5, 1, 2, 4, 8];

/**
 * Voyage Radar per-vessel track controller.
 *
 * Deliberately separate from `VoyageRadarPlaybackController`: that one replays
 * AOI snapshots (N vessels x M discrete instants, flip-book, no interpolation),
 * this one replays ONE vessel across N times and interpolates between reports
 * so the ghost vessel glides. Sharing a controller would mean sharing `_mode`,
 * `_steps` and `seek()` between two different datasets.
 *
 * Rendering stays in the map component: this controller owns the clock and the
 * state, and pushes frames out through `onRenderTrack`. See
 * plans/VOYAGE_RADAR_IMPLEMENTATION_PLAN.md (Phase 8C).
 */
@Injectable({ providedIn: 'root' })
export class VoyageTrackController extends PlaybackController {
  private _state$ = new BehaviorSubject<PlaybackState>({ ...EMPTY_PLAYBACK_STATE });
  readonly state$: Observable<PlaybackState> = this._state$.asObservable();

  /** Real-time tick interval; the virtual clock advances by rate x speed. */
  private static readonly TICK_MS = 100;
  /** Real milliseconds a full track takes to play at 1x. */
  private static readonly BASE_PLAY_MS = 24000;
  /** Upper bound on transport steps — keeps the slider and reprojection cheap. */
  private static readonly MAX_TRACK_STEPS = 2000;
  /**
   * How long a load may run before it is worth drawing.
   *
   * Under this the track simply appears, which is the best possible outcome and
   * needs no narration; a spinner that lives for 80 ms is a flicker that makes a
   * fast panel look unwell. 150 ms is the usual floor for "the user has noticed
   * nothing happened yet".
   */
  private static readonly LOADING_DELAY_MS = 150;
  /**
   * Once shown, the smallest time the pending row may live.
   *
   * Applies ONLY to a load that ends with nothing on the map — an error or an
   * empty result, where the spinner is followed by a dialog and would otherwise
   * blink out a frame before it. A load that succeeds clears the moment the
   * track lands (see `loadTrack`): the path arriving IS the completion signal,
   * and holding a pending row over a drawn track would put the panel in two
   * states at once.
   */
  private static readonly LOADING_MIN_MS = 400;

  // ── Active vessel ────────────────────────────────────────────────────────────
  private _active: VoyageActiveVessel | null = null;
  private _creditsRemaining: number | null = null;
  /** Catalogue price of one track; the shipped default until a sensor reports. */
  private _trackCost: number = VOYAGE_TRACK_CREDITS;
  /** TOI over — nothing here can be bought again. See `hasVoyageToiFinished`. */
  private _toiEnded = false;
  private _savedTracks: SavedTrackRef[] = [];
  private _trackLoading = false;
  /** The delayed, drawable form of `_trackLoading` — see LOADING_DELAY_MS. */
  private _trackLoadingVisible = false;
  private _pendingTrack: PendingTrackRef | null = null;
  private _loadingDelayTimer: any = null;
  private _loadingHoldTimer: any = null;
  /** When the pending row went on screen, for the minimum-hold arithmetic. */
  private _loadingShownAt = 0;
  private _trackSource: 'none' | 'full' | 'saved' = 'none';
  /** trackId of the loaded track — every track it plays is a persisted one. */
  private _activeTrackId: string | null = null;
  private _spanMinutes: number | null = null;

  // ── Track + transport ────────────────────────────────────────────────────────
  private _points: VoyageTrackPoint[] = [];
  private _times: number[] = [];
  /**
   * `_gaps[i]` — the leg from step i to i+1 was never observed. Parallel to
   * `_points`, last entry always false (nothing follows it). Read only by
   * `_renderAt`, to hold the ghost still across a leg the map does not draw.
   */
  private _gaps: boolean[] = [];
  /** Cached once per load — never rebuilt per emit (a track can be 2000 steps). */
  private _stepLabels: string[] = [];
  private _virtualTime = 0;
  private _currentIndex = 0;
  private _isPlaying = false;
  private _speed = 1;
  private _showLabels = false;
  /**
   * Whether the rest of the fleet is faded while this track is drawn. Lives on
   * the controller, not on the map controller, so the choice survives closing one
   * track and loading the next — it is a preference, not a property of a path.
   */
  private _dimOthers = true;
  /**
   * How many vessels on the map the dim would fade — reported by the map
   * controller, null while that is unknown. Not a property of the track: it is
   * here because the dim button lives on the track bar and has to know whether
   * there is anything behind the path to fade. See `setOthersOnMap`.
   */
  private _othersOnMap: number | null = null;
  private _playTimer: any = null;
  /** Virtual ms advanced per tick at 1x — derived from the track's own span. */
  private _rateMsPerTick = 1000;

  // ── Host callbacks (assigned by the map component) ───────────────────────────
  /** Draw the vessel at this instant (null = clear the track layers). */
  onRenderTrack: ((frame: VoyageTrackFrame | null) => void) | null = null;
  /** Build the track layers. Receives full geometry + the decimated transport points. */
  onTrackLoaded:
    | ((full: VoyageTrackPoint[], steps: VoyageTrackPoint[]) => void)
    | null = null;
  /** The active vessel changed (null = deselected) — drives the selection halo. */
  onActiveChange: ((vessel: VoyageActiveVessel | null) => void) | null = null;
  /** The user pressed the track toolbar's close button. */
  onClearActive: (() => void) | null = null;
  /** Buy the dense operator track over N minutes. */
  onRequestFull: ((minutes: number) => void) | null = null;
  /** Replay an already-purchased track (free). */
  onRequestSaved: ((trackId: string) => void) | null = null;
  /** Track layers were torn down — restore the live view. */
  onExit: (() => void) | null = null;
  /** Toggle the ghost vessel's permanent label. */
  onLabelsToggle: ((show: boolean) => void) | null = null;
  /** Fade (or restore) every live vessel the track is not about. */
  onDimOthersToggle: ((dim: boolean) => void) | null = null;

  constructor() {
    super();
    this._emit();
  }

  // ── Queries the template/host uses directly ──────────────────────────────────
  get activeMmsi(): string | null {
    return this._active?.mmsi ?? null;
  }

  get activeVessel(): VoyageActiveVessel | null {
    return this._active;
  }

  /** True once a track is loaded — gates the track toolbar host. */
  get hasTrack(): boolean {
    return this._points.length > 1;
  }

  snapshot(): PlaybackState {
    return this._state$.value;
  }

  private _emit(): void {
    this._state$.next({
      ...EMPTY_PLAYBACK_STATE,
      mode: this.hasTrack ? 'playback' : 'live',
      isPlaying: this._isPlaying,
      currentIndex: this._currentIndex,
      stepCount: this._points.length,
      stepLabels: this._stepLabels,
      speed: this._speed,
      showLabels: this._showLabels,
      dimOthers: this._dimOthers,
      activeMmsi: this._active?.mmsi ?? null,
      activeVesselName: this._active?.name ?? null,
      activeVesselColor: this._active?.color ?? null,
      trackSource: this._trackSource,
      activeTrackId: this._activeTrackId,
      trackLoading: this._trackLoading,
      trackLoadingVisible: this._trackLoadingVisible,
      pendingTrack: this._pendingTrack,
      trackSpanMinutes: this._spanMinutes,
      creditsRemaining: this._creditsRemaining,
      trackCost: this._trackCost,
      toiEnded: this._toiEnded,
      savedTracks: this._savedTracks,
      othersOnMap: this._othersOnMap,
    });
  }

  // ── Selection ────────────────────────────────────────────────────────────────
  /** Select (or clear) the vessel the toolbar and popup act on. */
  setActiveVessel(vessel: VoyageActiveVessel | null): void {
    this._clearTrack(false);
    this._active = vessel;
    this._trackSource = 'none';
    this._activeTrackId = null;
    this._spanMinutes = null;
    this.onActiveChange?.(vessel);
    this._emit();
  }

  /**
   * Remaining API credits in the wallet, what a track costs against it, and
   * whether the TOI has closed — all three drive the cost line and the
   * disabled chips.
   *
   * Set together on purpose: they are read from the same sensor at the same
   * moment, so a caller cannot refresh one and leave the others stale.
   */
  setCreditsRemaining(n: number | null, trackCost: number, toiEnded: boolean): void {
    this._creditsRemaining = n;
    this._trackCost = trackCost;
    this._toiEnded = toiEnded;
    this._emit();
  }

  /** Tracks already purchased for the active vessel (replayable for free). */
  setSavedTracks(tracks: SavedTrackRef[]): void {
    this._savedTracks = tracks ?? [];
    this._emit();
  }

  /**
   * Open or close a track load.
   *
   * Two flags come out of this, and they are not the same fact. `trackLoading`
   * is the truth, and is what gates the second click — a load in flight must
   * stay un-clickable from the first millisecond or a double-click buys two
   * operator calls. `trackLoadingVisible` is what may be DRAWN, and it lags by
   * `LOADING_DELAY_MS` so that a fast load never blinks a spinner at the user.
   * Every gate should read the first; every template the second.
   *
   * `pending` describes what is being loaded, so the wait can name the hull and
   * its point count instead of showing an anonymous spinner. Ignored on close.
   */
  setTrackLoading(loading: boolean, pending: PendingTrackRef | null = null): void {
    this._trackLoading = loading;
    if (loading) {
      this._pendingTrack = pending;
      this._armLoadingReveal();
      this._emit();
      return;
    }
    this._closeLoadingReveal();
    this._emit();
  }

  /** Starts the delay after which a load becomes worth drawing. */
  private _armLoadingReveal(): void {
    this._clearLoadingTimers();
    if (this._trackLoadingVisible) return; // already on screen from a prior load
    this._loadingDelayTimer = setTimeout(() => {
      this._loadingDelayTimer = null;
      // Re-checked rather than assumed: `loadTrack` can land inside the delay
      // and clear the load without this timer having been the one to fire.
      if (!this._trackLoading) return;
      this._trackLoadingVisible = true;
      this._loadingShownAt = Date.now();
      this._emit();
    }, VoyageTrackController.LOADING_DELAY_MS);
  }

  /**
   * Ends a load that put nothing on the map. Holds the pending row to
   * `LOADING_MIN_MS` when it is already showing, so the spinner does not blink
   * out one frame before the dialog that explains why.
   */
  private _closeLoadingReveal(): void {
    this._clearLoadingTimers();
    if (!this._trackLoadingVisible) {
      this._pendingTrack = null;
      return;
    }
    const shownFor = Date.now() - this._loadingShownAt;
    const left = VoyageTrackController.LOADING_MIN_MS - shownFor;
    if (left <= 0) {
      this._hideLoadingNow(false);
      return;
    }
    this._loadingHoldTimer = setTimeout(() => {
      this._loadingHoldTimer = null;
      // A new load may have opened during the hold — it owns the row now.
      if (this._trackLoading) return;
      this._hideLoadingNow(true);
    }, left);
  }

  /**
   * Takes the pending row down at once, whatever the hold had left.
   *
   * Called by `loadTrack`: the path appearing on the map is a completion signal
   * no spinner can improve on, and leaving the row up over it would have the
   * panel claiming a load is in flight while the map draws its result.
   */
  private _hideLoadingNow(emit: boolean): void {
    this._clearLoadingTimers();
    this._trackLoadingVisible = false;
    this._pendingTrack = null;
    this._loadingShownAt = 0;
    if (emit) this._emit();
  }

  private _clearLoadingTimers(): void {
    if (this._loadingDelayTimer) {
      clearTimeout(this._loadingDelayTimer);
      this._loadingDelayTimer = null;
    }
    if (this._loadingHoldTimer) {
      clearTimeout(this._loadingHoldTimer);
      this._loadingHoldTimer = null;
    }
  }

  /**
   * How many vessels on the map are NOT the traced one (0 when the map is empty
   * or holds only that hull, null when unknown). Pushed by the map controller
   * whenever the live layer or the selection changes.
   *
   * Deliberately NOT cleared by `_clearTrack`: it describes the MAP, which
   * outlives any one track. Guarded against re-emitting an unchanged count —
   * `setActiveVessel` pushes it on every selection, most of which do not move it.
   */
  setOthersOnMap(n: number | null): void {
    if (this._othersOnMap === n) return;
    this._othersOnMap = n;
    this._emit();
  }

  setSpan(minutes: number | null): void {
    this._spanMinutes = minutes;
    this._emit();
  }

  // ── Optional-hook overrides the toolbar controls call ────────────────────────
  override clearActiveVessel(): void {
    this.close();
    this.onClearActive?.();
  }

  override requestFullTrack(minutes: number): void {
    this.onRequestFull?.(minutes);
  }

  override requestSavedTrack(trackId: string): void {
    this.onRequestSaved?.(trackId);
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  /**
   * Install a track and start at its first point. `points` may be dense — the
   * transport is decimated to `MAX_TRACK_STEPS` while the host still draws the
   * full geometry, so a 90-day track scrubs as smoothly as a 1-hour one.
   */
  loadTrack(
    points: VoyageTrackPoint[],
    source: 'full' | 'saved',
    trackId: string | null = null
  ): number {
    this.pause();
    const full = (points ?? [])
      .filter((p) => p && isFinite(p.lat) && isFinite(p.lng) && !!p.timeIso)
      .sort((a, b) => new Date(a.timeIso).getTime() - new Date(b.timeIso).getTime());
    if (full.length < 2) {
      this._clearTrack(true);
      return full.length;
    }
    const steps = VoyageTrackController._decimate(full, VoyageTrackController.MAX_TRACK_STEPS);
    this._points = steps;
    this._times = steps.map((p) => new Date(p.timeIso).getTime());
    // Computed on the DECIMATED list, not on `full`, because these flags answer
    // a question about the pairs this transport actually steps between. The two
    // can differ: dropping points widens the interval between the survivors, so
    // a run that was continuous at full resolution may legitimately read as a
    // gap here — and the ghost must not glide across a leg the renderer has
    // already lifted its ink from. `buildTrackLayers` splits `full` and `steps`
    // separately for the same reason.
    this._gaps = steps.map((p, i) => (i + 1 < steps.length ? isTrackGap(p, steps[i + 1]) : false));
    this._stepLabels = steps.map((p) => p.timeIso);
    this._trackSource = source;
    this._activeTrackId = trackId;
    this._trackLoading = false;
    // Not held to the minimum: the track is about to be drawn, and that is a
    // better completion signal than any spinner. See `_hideLoadingNow`.
    this._hideLoadingNow(false);
    this._currentIndex = 0;
    this._virtualTime = this._times[0];
    // 1x plays the whole track in BASE_PLAY_MS regardless of its real duration.
    const spanMs = Math.max(1, this._times[this._times.length - 1] - this._times[0]);
    this._rateMsPerTick =
      (spanMs * VoyageTrackController.TICK_MS) / VoyageTrackController.BASE_PLAY_MS;
    this._speed = 1;
    this.onTrackLoaded?.(full, steps);
    this._renderAt(this._virtualTime);
    this._emit();
    return steps.length;
  }

  /** Uniform-stride decimation that always keeps the first and last points. */
  private static _decimate(points: VoyageTrackPoint[], max: number): VoyageTrackPoint[] {
    if (points.length <= max) return points;
    const stride = (points.length - 1) / (max - 1);
    const out: VoyageTrackPoint[] = [];
    for (let i = 0; i < max - 1; i++) out.push(points[Math.round(i * stride)]);
    out.push(points[points.length - 1]);
    return out;
  }

  // ── Transport ────────────────────────────────────────────────────────────────
  togglePlay(): void {
    this._isPlaying ? this.pause() : this.play();
  }

  play(): void {
    if (this._isPlaying || this._points.length < 2) return;
    if (this._virtualTime >= this._lastTime()) this._virtualTime = this._times[0];
    this._isPlaying = true;
    this._playTimer = setInterval(() => this._tick(), VoyageTrackController.TICK_MS);
    this._emit();
  }

  pause(): void {
    this._isPlaying = false;
    if (this._playTimer) {
      clearInterval(this._playTimer);
      this._playTimer = null;
    }
    this._emit();
  }

  seek(index: number): void {
    if (!this._points.length) return;
    const i = Math.max(0, Math.min(index, this._points.length - 1));
    this._currentIndex = i;
    this._virtualTime = this._times[i];
    this._renderAt(this._virtualTime);
    this._emit();
  }

  stepForward(): void {
    this.seek(this._currentIndex + 1);
  }
  stepBack(): void {
    this.seek(this._currentIndex - 1);
  }
  skipToStart(): void {
    this.seek(0);
  }
  skipToEnd(): void {
    this.seek(this._points.length - 1);
  }

  setSpeed(speed: number): void {
    this._speed = speed;
    this._emit();
  }

  toggleLabels(): void {
    this._showLabels = !this._showLabels;
    this.onLabelsToggle?.(this._showLabels);
    this._emit();
  }

  /**
   * Show the surrounding traffic at full strength (or fade it back down).
   *
   * The dim is what makes a single path readable in an AOI holding hundreds of
   * hulls, but it is also what hides the vessels a track is often bought to be
   * compared against — so it is a button rather than a rule.
   */
  override toggleDimOthers(): void {
    this._dimOthers = !this._dimOthers;
    this.onDimOthersToggle?.(this._dimOthers);
    this._emit();
  }

  /** Current dim preference — read by the host when it builds the track layers. */
  get dimOthers(): boolean {
    return this._dimOthers;
  }

  /** Single vessel — type filtering is meaningless here. */
  setTypeFilter(_set: Set<string>): void {
    /* no-op */
  }

  /** Track mode is entered by selecting a vessel, never by a date range. */
  enterPlayback(_fromIso: string, _toIso: string): Promise<number> {
    return Promise.resolve(0);
  }

  resumeLive(): void {
    this.close();
    this.onExit?.();
  }

  close(): void {
    this.pause();
    this._clearTrack(true);
  }

  // ── Internals ────────────────────────────────────────────────────────────────
  private _clearTrack(emit: boolean): void {
    // Stop the clock first: a tick against an empty track would publish
    // currentIndex = -1 to the timeline before self-pausing.
    this.pause();
    this._points = [];
    this._times = [];
    this._gaps = [];
    this._stepLabels = [];
    this._currentIndex = 0;
    this._virtualTime = 0;
    this._trackSource = 'none';
    this._activeTrackId = null;
    this._trackLoading = false;
    // Through the reveal rather than straight at the flag: dropping the truth
    // without closing its timers would strand a pending row on screen with no
    // load behind it and nothing left to take it down.
    this._hideLoadingNow(false);
    this.onRenderTrack?.(null);
    if (emit) this._emit();
  }

  private _lastTime(): number {
    return this._times[this._times.length - 1] ?? 0;
  }

  private _indexAtTime(vt: number): number {
    let i = 0;
    while (i + 1 < this._times.length && this._times[i + 1] <= vt) i++;
    return i;
  }

  private _tick(): void {
    const prevIndex = this._currentIndex;
    this._virtualTime += this._rateMsPerTick * this._speed;
    const end = this._lastTime();
    if (this._virtualTime >= end) {
      this._virtualTime = end;
      this._currentIndex = this._points.length - 1;
      this._renderAt(this._virtualTime);
      this.pause();
      return;
    }
    this._currentIndex = this._indexAtTime(this._virtualTime);
    // Render every tick (that is what makes the ghost glide) but only publish
    // state when the visible step changes — otherwise every control in the
    // toolbar runs change detection 10x a second.
    this._renderAt(this._virtualTime);
    if (this._currentIndex !== prevIndex) this._emit();
  }

  /** Interpolate the vessel's position/heading at a virtual instant. */
  private _renderAt(vt: number): void {
    if (!this._points.length) return;
    const i = this._indexAtTime(vt);
    const p0 = this._points[i];
    if (!p0) return;
    const p1 = this._points[i + 1];
    const t0 = this._times[i];
    const t1 = this._times[i + 1];
    // ACROSS A GAP THE GHOST DOES NOT MOVE. `_gaps[i]` says the leg ahead was
    // never observed, so interpolating along it would animate a voyage nobody
    // recorded — the arrow used to cross 1,056 km of Tamil Nadu at a plausible
    // 6 kn, which is a far more convincing lie than the static line was. It now
    // holds the last known fix while the clock runs out the dark period, then
    // arrives at the next report the instant that report exists. The transport
    // is unaffected: the clock still spends the real duration on the gap, which
    // is what makes the wait itself read as "no contact".
    const gapAhead = !!this._gaps[i];
    const frac =
      p1 && t1 > t0 && !gapAhead ? Math.min(1, Math.max(0, (vt - t0) / (t1 - t0))) : 0;
    const lat = p1 ? p0.lat + (p1.lat - p0.lat) * frac : p0.lat;
    const lng = p1 ? p0.lng + (p1.lng - p0.lng) * frac : p0.lng;
    // Shortest-angle interpolation so the ghost never spins through 359 -> 1.
    // Held at 0 across a gap by the same `frac`, so the arrow keeps the heading
    // it was last seen on rather than swinging toward a bearing it never sailed.
    let course = p0.course;
    if (p1) {
      const delta = ((p1.course - p0.course + 540) % 360) - 180;
      course = (p0.course + delta * frac + 360) % 360;
    }
    this.onRenderTrack?.({
      lat,
      lng,
      course,
      index: i,
      frac,
      timeIso: p0.timeIso,
      atEnd: !p1,
    });
  }
}
